import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, Card, StatusBadge, LoadingSpinner } from '../components/Layout';
import clsx from 'clsx';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface SalesRep {
  id: string;
  first_name: string;
  last_name: string;
  employee_id: string;
  email: string;
  phone?: string;
  commission_rate_override?: number;
  status: string;
  assigned_dealer_count?: number;
  created_at: string;
}

interface Commission {
  id: string;
  order_id: string;
  order_number: string;
  sales_rep_id: string;
  rep_first_name: string;
  rep_last_name: string;
  is_assist: boolean;
  commission_percent: number;
  base_amount: number;
  commission_amount: number;
  status: string;
  clawback_reason?: string;
  company_name?: string;
  created_at: string;
  paid_at?: string;
}

interface Dealer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  company?: string;
  sales_rep_id?: string;
  discount_percent?: number;
}

async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('admin_auth_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  return response.json();
}

type TabType = 'reps' | 'commissions' | 'splits' | 'clawbacks' | 'leads' | 'payouts-rep' | 'payouts-internal';

interface CommissionSplit {
  id: string;
  order_id: string;
  order_number: string;
  order_status: string;
  order_total: number;
  subtotal: number;
  dealer_company?: string;
  dealer_first_name?: string;
  dealer_last_name?: string;
  dealer_type?: string;
  total_commission_percent: number;
  total_commission_amount: number;
  splits: SplitEntry[];
  locked_at?: string;
  locked_by?: string;
  paid_at?: string;
  created_at: string;
}

interface SplitEntry {
  sales_rep_id: string;
  percent: number;
  amount: number;
  locked: boolean;
  role?: string;
  name?: string;
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'px-4 py-2 rounded-lg font-medium transition-colors',
        active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      )}
    >
      {children}
    </button>
  );
}

function Modal({ isOpen, onClose, title, children }: { isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

// Sales Reps Tab
function SalesRepsTab() {
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRep, setSelectedRep] = useState<SalesRep | null>(null);
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    loadSalesReps();
  }, []);

  const loadSalesReps = async () => {
    try {
      const data = await apiRequest('/users/sales-rep');
      setSalesReps(data.users || []);
    } catch (err) {
      console.error('Failed to load sales reps:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRepStats = async (repId: string) => {
    try {
      const data = await apiRequest(`/users/sales-rep/${repId}/stats`);
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleViewDetails = (rep: SalesRep) => {
    setSelectedRep(rep);
    loadRepStats(rep.id);
  };

  const handleImpersonate = async (rep: SalesRep) => {
    if (!confirm(`Open ${rep.first_name} ${rep.last_name}'s sales portal view in a new tab?`)) return;
    try {
      const { access_token } = await apiRequest(`/sales-portal/admin/impersonate/${rep.id}`, {
        method: 'POST',
      });
      const salesUrl = (import.meta as any).env?.VITE_SALES_PORTAL_URL || 'https://sales.kederscreens.com';
      window.open(`${salesUrl}?impersonate_token=${encodeURIComponent(access_token)}`, '_blank');
    } catch (err: any) {
      alert(err?.message || 'Failed to impersonate sales rep');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><LoadingSpinner /></div>;
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-gray-500 text-sm border-b">
              <th className="pb-3 font-medium">Name</th>
              <th className="pb-3 font-medium">Employee ID</th>
              <th className="pb-3 font-medium">Email</th>
              <th className="pb-3 font-medium">Dealers</th>
              <th className="pb-3 font-medium">Commission Rate</th>
              <th className="pb-3 font-medium">Status</th>
              <th className="pb-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {salesReps.map((rep) => (
              <tr key={rep.id} className="border-b hover:bg-gray-50">
                <td className="py-3 font-medium">{rep.first_name} {rep.last_name}</td>
                <td className="py-3">{rep.employee_id}</td>
                <td className="py-3 text-gray-600">{rep.email}</td>
                <td className="py-3">{rep.assigned_dealer_count || 0}</td>
                <td className="py-3">
                  {rep.commission_rate_override ? `${rep.commission_rate_override}%` : 'Default'}
                </td>
                <td className="py-3">
                  <StatusBadge status={rep.status} />
                </td>
                <td className="py-3">
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleViewDetails(rep)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => handleImpersonate(rep)}
                      className="text-purple-600 hover:text-purple-800 font-medium"
                      title={`Open ${rep.first_name}'s sales portal view in a new tab`}
                    >
                      Login As
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={!!selectedRep} onClose={() => { setSelectedRep(null); setStats(null); }} title={selectedRep ? `${selectedRep.first_name} ${selectedRep.last_name}` : ''}>
        {selectedRep && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <p className="text-2xl font-bold text-gray-900">${Number(stats.commissions?.this_month || 0).toLocaleString()}</p>
                <p className="text-sm text-gray-500">This Month</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <p className="text-2xl font-bold text-gray-900">${Number(stats.commissions?.this_quarter || 0).toLocaleString()}</p>
                <p className="text-sm text-gray-500">This Quarter</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <p className="text-2xl font-bold text-gray-900">${Number(stats.commissions?.ytd || 0).toLocaleString()}</p>
                <p className="text-sm text-gray-500">YTD</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-yellow-50 p-4 rounded-lg text-center">
                <p className="text-xl font-bold text-yellow-700">${Number(stats.commissions?.pending || 0).toLocaleString()}</p>
                <p className="text-sm text-yellow-600">Pending</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg text-center">
                <p className="text-xl font-bold text-green-700">${Number(stats.commissions?.earned || 0).toLocaleString()}</p>
                <p className="text-sm text-green-600">Earned</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg text-center">
                <p className="text-xl font-bold text-blue-700">${Number(stats.commissions?.paid || 0).toLocaleString()}</p>
                <p className="text-sm text-blue-600">Paid</p>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Assigned Dealers ({stats.dealer_count})</h3>
              <p className="text-gray-500 text-sm">View on Dealers page</p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Recent Orders</h3>
              {stats.recent_orders?.length > 0 ? (
                <div className="space-y-2">
                  {stats.recent_orders.slice(0, 5).map((order: any) => (
                    <div key={order.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <div>
                        {order.id ? (
                          <Link to={`/orders/${order.id}`} className="font-medium text-blue-600 hover:text-blue-800 hover:underline">
                            {order.order_number}
                          </Link>
                        ) : (
                          <span className="font-medium">{order.order_number}</span>
                        )}
                        <span className="text-gray-500 text-sm ml-2">{order.company}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-medium">${Number(order.total_amount || 0).toLocaleString()}</span>
                        <StatusBadge status={order.status} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No recent orders</p>
              )}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

// Commissions Tab
function CommissionsTab() {
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedCommissions, setSelectedCommissions] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  useEffect(() => {
    loadCommissions();
  }, [statusFilter]);

  const loadCommissions = async () => {
    try {
      setLoading(true);
      let endpoint = '/commissions';
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (params.toString()) endpoint += `?${params.toString()}`;

      const data = await apiRequest(endpoint);
      setCommissions(data.commissions || []);
    } catch (err) {
      console.error('Failed to load commissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (selectedCommissions.length === 0) return;
    try {
      await apiRequest('/commissions/mark-paid', {
        method: 'POST',
        body: JSON.stringify({ commission_ids: selectedCommissions }),
      });
      setSelectedCommissions([]);
      loadCommissions();
    } catch (err) {
      console.error('Failed to mark as paid:', err);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedCommissions(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAllEarned = () => {
    const earnedIds = commissions.filter(c => c.status === 'earned').map(c => c.id);
    setSelectedCommissions(earnedIds);
  };

  const exportCSV = async () => {
    if (!dateRange.start || !dateRange.end) {
      alert('Please select a date range');
      return;
    }
    try {
      const data = await apiRequest(`/commissions/report?start_date=${dateRange.start}&end_date=${dateRange.end}`);
      // Convert to CSV
      const headers = ['Sales Rep', 'Employee ID', 'Pending', 'Earned', 'Paid', 'Total Sales'];
      const rows = data.map((r: any) => [
        `${r.first_name} ${r.last_name}`,
        r.employee_id,
        r.pending_amount,
        r.earned_amount,
        r.paid_amount,
        r.total_sales,
      ]);

      const csv = [headers.join(','), ...rows.map((r: any[]) => r.join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commissions-${dateRange.start}-to-${dateRange.end}.csv`;
      a.click();
    } catch (err) {
      console.error('Failed to export:', err);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="earned">Earned</option>
            <option value="paid">Paid</option>
          </select>
        </div>

        <div className="flex gap-2 items-center">
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          />
          <span>to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          />
          <button
            onClick={exportCSV}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Export CSV
          </button>
        </div>
      </div>

      {selectedCommissions.length > 0 && (
        <div className="bg-blue-50 p-3 rounded-lg flex items-center justify-between">
          <span className="text-blue-700">{selectedCommissions.length} commission(s) selected</span>
          <button
            onClick={handleMarkPaid}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Mark as Paid
          </button>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <button
          onClick={selectAllEarned}
          className="text-sm text-blue-600 hover:underline"
        >
          Select all earned
        </button>
        <button
          onClick={() => setSelectedCommissions([])}
          className="text-sm text-gray-600 hover:underline"
        >
          Clear selection
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-gray-500 text-sm border-b">
              <th className="pb-3 font-medium w-8">
                <input
                  type="checkbox"
                  checked={selectedCommissions.length === commissions.filter(c => c.status === 'earned').length}
                  onChange={selectAllEarned}
                  className="rounded"
                />
              </th>
              <th className="pb-3 font-medium">Order</th>
              <th className="pb-3 font-medium">Sales Rep</th>
              <th className="pb-3 font-medium">Dealer</th>
              <th className="pb-3 font-medium">Base Amount</th>
              <th className="pb-3 font-medium">Commission</th>
              <th className="pb-3 font-medium">Status</th>
              <th className="pb-3 font-medium">Date</th>
            </tr>
          </thead>
          <tbody>
            {commissions.map((comm) => (
              <tr key={comm.id} className="border-b hover:bg-gray-50">
                <td className="py-3">
                  {comm.status === 'earned' && (
                    <input
                      type="checkbox"
                      checked={selectedCommissions.includes(comm.id)}
                      onChange={() => toggleSelect(comm.id)}
                      className="rounded"
                    />
                  )}
                </td>
                <td className="py-3 font-medium">
                  {comm.order_id ? (
                    <Link to={`/orders/${comm.order_id}`} className="text-blue-600 hover:text-blue-800 hover:underline">
                      {comm.order_number}
                    </Link>
                  ) : (
                    comm.order_number
                  )}
                </td>
                <td className="py-3">
                  {comm.rep_first_name} {comm.rep_last_name}
                  {comm.is_assist && <span className="ml-1 text-xs text-purple-600">(Assist)</span>}
                </td>
                <td className="py-3 text-gray-600">{comm.company_name || '-'}</td>
                <td className="py-3">${Number(comm.base_amount).toLocaleString()}</td>
                <td className="py-3 font-medium">${Number(comm.commission_amount).toLocaleString()}</td>
                <td className="py-3">
                  <StatusBadge status={comm.status} />
                </td>
                <td className="py-3 text-gray-500 text-sm">
                  {new Date(comm.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Clawbacks Tab
function ClawbacksTab() {
  const [clawbacks, setClawbacks] = useState<Commission[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [salesReps, setSalesReps] = useState<any[]>([]);
  // Form state
  const [orderNumber, setOrderNumber] = useState('');
  const [selectedRepId, setSelectedRepId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    loadClawbacks();
    loadSalesReps();
  }, []);

  const loadSalesReps = async () => {
    try {
      const data = await apiRequest('/users?role=sales_rep');
      // Fallback to sales rep endpoint shape
      setSalesReps(Array.isArray(data) ? data : (data?.users || data?.items || []));
    } catch {
      try {
        const alt = await apiRequest('/sales-rep-users');
        setSalesReps(Array.isArray(alt) ? alt : (alt?.users || alt?.items || []));
      } catch (e) {
        console.error('Failed to load sales reps:', e);
      }
    }
  };

  const submitClawback = async () => {
    setFormError(null);
    if (!orderNumber.trim()) return setFormError('Order number required');
    if (!selectedRepId) return setFormError('Pick a sales rep');
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) return setFormError('Amount must be > 0');
    if (!reason.trim()) return setFormError('Reason required');
    setSubmitting(true);
    try {
      // Resolve order number → order_id
      const order = await apiRequest(`/orders/${encodeURIComponent(orderNumber.trim())}`);
      const orderId = order?.id;
      if (!orderId) {
        setFormError(`Order ${orderNumber} not found`);
        setSubmitting(false);
        return;
      }
      await apiRequest('/rep-firms/commissions/adjustment', {
        method: 'POST',
        body: JSON.stringify({
          order_id: orderId,
          sales_rep_id: selectedRepId,
          amount: -Math.abs(amt), // always negative for clawback
          note: reason.trim(),
        }),
      });
      // Reset + reload
      setOrderNumber(''); setSelectedRepId(''); setAmount(''); setReason('');
      setShowForm(false);
      await loadClawbacks();
    } catch (err: any) {
      setFormError(err?.message || 'Failed to record clawback');
    } finally {
      setSubmitting(false);
    }
  };

  const loadClawbacks = async () => {
    try {
      const data = await apiRequest('/commissions/clawbacks');
      setClawbacks(data.commissions || []);
    } catch (err) {
      console.error('Failed to load clawbacks:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><LoadingSpinner /></div>;
  }

  return (
    <div>
      {/* Add Clawback button + collapsible form */}
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700"
        >
          {showForm ? 'Cancel' : '+ Add Clawback'}
        </button>
      </div>
      {showForm && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded">
          <h3 className="text-sm font-semibold mb-3">New Clawback / Negative Adjustment</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Order Number</label>
              <input
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="e.g. VT26050006"
                className="w-full px-2 py-1.5 border rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Sales Rep</label>
              <select
                value={selectedRepId}
                onChange={(e) => setSelectedRepId(e.target.value)}
                className="w-full px-2 py-1.5 border rounded text-sm"
              >
                <option value="">— Pick rep —</option>
                {salesReps.map((r: any) => (
                  <option key={r.id} value={r.id}>{r.first_name || r.firstName} {r.last_name || r.lastName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Amount ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="16.42"
                className="w-full px-2 py-1.5 border rounded text-sm"
              />
              <div className="text-xs text-gray-500 mt-1">Enter positive — recorded as negative.</div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Reason</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Order marked exempt after payment"
                className="w-full px-2 py-1.5 border rounded text-sm"
              />
            </div>
          </div>
          {formError && <div className="text-sm text-red-600 mb-2">{formError}</div>}
          <button
            type="button"
            onClick={submitClawback}
            disabled={submitting}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 disabled:opacity-50"
          >
            {submitting ? 'Recording…' : 'Record Clawback'}
          </button>
        </div>
      )}
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="text-left text-gray-500 text-sm border-b">
            <th className="pb-3 font-medium">Order</th>
            <th className="pb-3 font-medium">Sales Rep</th>
            <th className="pb-3 font-medium">Original Commission</th>
            <th className="pb-3 font-medium">Reason</th>
            <th className="pb-3 font-medium">Date</th>
          </tr>
        </thead>
        <tbody>
          {clawbacks.length === 0 ? (
            <tr>
              <td colSpan={5} className="py-8 text-center text-gray-500">
                No clawbacks found
              </td>
            </tr>
          ) : (
            clawbacks.map((comm: any) => {
              // Two shapes land in this list:
              //   1. status='clawback'  → original row, amount zeroed, base*% has the original
              //   2. negative adjustment → commission_amount holds the (negative) delta directly
              const isAdjustment = comm.commission_level === 'adjustment';
              const amount = isAdjustment
                ? Math.abs(Number(comm.commission_amount || 0))
                : Number((comm.base_amount || 0) * ((comm.commission_percent || 0) / 100));
              const repName = comm.sales_rep_name
                || [comm.rep_first_name, comm.rep_last_name].filter(Boolean).join(' ')
                || comm.rep_firm_name
                || '—';
              return (
                <tr key={comm.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 font-medium">
                    {comm.order_id ? (
                      <Link to={`/orders/${comm.order_id}`} className="text-blue-600 hover:text-blue-800 hover:underline">
                        {comm.order_number || '—'}
                      </Link>
                    ) : (
                      comm.order_number || '—'
                    )}
                  </td>
                  <td className="py-3">{repName}</td>
                  <td className="py-3 text-red-600 line-through">
                    ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="py-3 text-gray-600">{comm.clawback_reason || (isAdjustment ? 'Negative adjustment' : 'Order cancelled/returned')}</td>
                  <td className="py-3 text-gray-500 text-sm">
                    {new Date(comm.created_at).toLocaleDateString()}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
    </div>
  );
}

// Reassign Dealers Tab
function ReassignDealersTab() {
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDealer, setSelectedDealer] = useState<string | null>(null);
  const [newRepId, setNewRepId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [filterRep, setFilterRep] = useState<string>('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dealersData, repsData] = await Promise.all([
        apiRequest('/users/dealer-portal'),
        apiRequest('/users/sales-rep/active'),
      ]);
      setDealers(dealersData.users || []);
      setSalesReps(repsData || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReassign = async () => {
    if (!selectedDealer) return;
    setSaving(true);
    try {
      if (newRepId) {
        await apiRequest(`/users/dealer-portal/${selectedDealer}/assign-sales-rep`, {
          method: 'POST',
          body: JSON.stringify({ sales_rep_id: newRepId }),
        });
      } else {
        await apiRequest(`/users/dealer-portal/${selectedDealer}/sales-rep`, {
          method: 'DELETE',
        });
      }
      setSelectedDealer(null);
      setNewRepId('');
      loadData();
    } catch (err) {
      console.error('Failed to reassign:', err);
    } finally {
      setSaving(false);
    }
  };

  const getSalesRepName = (repId: string | undefined) => {
    if (!repId) return 'Unassigned';
    const rep = salesReps.find(r => r.id === repId);
    return rep ? `${rep.first_name} ${rep.last_name}` : 'Unknown';
  };

  const filteredDealers = filterRep
    ? filterRep === 'unassigned'
      ? dealers.filter(d => !d.sales_rep_id)
      : dealers.filter(d => d.sales_rep_id === filterRep)
    : dealers;

  if (loading) {
    return <div className="flex justify-center py-8"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-4 items-center">
        <select
          value={filterRep}
          onChange={(e) => setFilterRep(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value="">All Sales Reps</option>
          <option value="unassigned">Unassigned</option>
          {salesReps.map(rep => (
            <option key={rep.id} value={rep.id}>
              {rep.first_name} {rep.last_name}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-gray-500 text-sm border-b">
              <th className="pb-3 font-medium">Dealer</th>
              <th className="pb-3 font-medium">Company</th>
              <th className="pb-3 font-medium">Current Sales Rep</th>
              <th className="pb-3 font-medium">Discount</th>
              <th className="pb-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDealers.map((dealer) => (
              <tr key={dealer.id} className="border-b hover:bg-gray-50">
                <td className="py-3">
                  <div>
                    <span className="font-medium">{dealer.first_name} {dealer.last_name}</span>
                    <p className="text-sm text-gray-500">{dealer.email}</p>
                  </div>
                </td>
                <td className="py-3">{dealer.company || '-'}</td>
                <td className="py-3">
                  <span className={dealer.sales_rep_id ? 'text-blue-600' : 'text-orange-600'}>
                    {getSalesRepName(dealer.sales_rep_id)}
                  </span>
                </td>
                <td className="py-3">{dealer.discount_percent || 0}%</td>
                <td className="py-3">
                  {selectedDealer === dealer.id ? (
                    <div className="flex gap-2 items-center">
                      <select
                        value={newRepId}
                        onChange={(e) => setNewRepId(e.target.value)}
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="">Unassigned</option>
                        {salesReps.map(rep => (
                          <option key={rep.id} value={rep.id}>
                            {rep.first_name} {rep.last_name}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={handleReassign}
                        disabled={saving}
                        className="text-green-600 hover:text-green-800 disabled:opacity-50"
                      >
                        {saving ? '...' : 'Save'}
                      </button>
                      <button
                        onClick={() => setSelectedDealer(null)}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setSelectedDealer(dealer.id);
                        setNewRepId(dealer.sales_rep_id || '');
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Reassign
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Commission Splits Tab
function CommissionSplitsTab() {
  const [splits, setSplits] = useState<CommissionSplit[]>([]);
  const [loading, setLoading] = useState(true);
  const [lockedFilter, setLockedFilter] = useState<string>('');
  const [paidFilter, setPaidFilter] = useState<string>('');
  const [teamFilter, setTeamFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedSplits, setSelectedSplits] = useState<string[]>([]);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  useEffect(() => {
    loadSplits();
  }, [lockedFilter, paidFilter, teamFilter]);

  const loadSplits = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (lockedFilter === 'locked') params.append('locked', 'true');
      if (lockedFilter === 'unlocked') params.append('locked', 'false');
      if (paidFilter === 'paid') params.append('paid', 'true');
      if (paidFilter === 'unpaid') params.append('paid', 'false');
      if (teamFilter) params.append('team', teamFilter);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      params.append('limit', '100');

      const data = await apiRequest(`/commission-splits?${params.toString()}`);
      setSplits(data.splits || []);
    } catch (err) {
      console.error('Failed to load commission splits:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (selectedSplits.length === 0) return;
    try {
      await apiRequest('/commission-splits/mark-paid', {
        method: 'POST',
        body: JSON.stringify({ split_ids: selectedSplits }),
      });
      setSelectedSplits([]);
      loadSplits();
    } catch (err) {
      console.error('Failed to mark as paid:', err);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedSplits(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAllLocked = () => {
    const lockedIds = splits.filter(s => s.locked_at && !s.paid_at).map(s => s.id);
    setSelectedSplits(lockedIds);
  };

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (lockedFilter === 'locked') params.append('locked', 'true');
      if (lockedFilter === 'unlocked') params.append('locked', 'false');
      if (paidFilter === 'paid') params.append('paid', 'true');
      if (paidFilter === 'unpaid') params.append('paid', 'false');
      if (teamFilter) params.append('team', teamFilter);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);

      const response = await fetch(`${API_BASE}/commission-splits/export?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_auth_token')}`,
        },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commission-splits-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (err) {
      console.error('Failed to export:', err);
    }
  };

  const getRoleBadgeColor = (role?: string) => {
    switch (role) {
      case 'vp_of_sales': return 'bg-purple-100 text-purple-800';
      case 'regional_director': return 'bg-blue-100 text-blue-800';
      case 'territory_manager': return 'bg-green-100 text-green-800';
      case 'sales_rep': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatRole = (role?: string) => {
    switch (role) {
      case 'vp_of_sales': return 'VP';
      case 'regional_director': return 'RD';
      case 'territory_manager': return 'TM';
      case 'sales_rep': return 'Rep';
      default: return role || '';
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          <select
            value={lockedFilter}
            onChange={(e) => setLockedFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All Lock Status</option>
            <option value="locked">Locked</option>
            <option value="unlocked">Unlocked (Needs Attention)</option>
          </select>

          <select
            value={paidFilter}
            onChange={(e) => setPaidFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All Payment Status</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>

          <select
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All Teams</option>
            <option value="core_window_treatment">Core Window Treatment</option>
            <option value="cedia_industry">CEDIA Industry</option>
          </select>
        </div>

        <div className="flex gap-2 items-center">
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <button
            onClick={loadSplits}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
          >
            Filter
          </button>
          <button
            onClick={exportCSV}
            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
          >
            Export CSV
          </button>
        </div>
      </div>

      {selectedSplits.length > 0 && (
        <div className="bg-blue-50 p-3 rounded-lg flex items-center justify-between">
          <span className="text-blue-700">{selectedSplits.length} split(s) selected</span>
          <button
            onClick={handleMarkPaid}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Mark as Paid
          </button>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <button
          onClick={selectAllLocked}
          className="text-sm text-blue-600 hover:underline"
        >
          Select all locked (unpaid)
        </button>
        <button
          onClick={() => setSelectedSplits([])}
          className="text-sm text-gray-600 hover:underline"
        >
          Clear selection
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-gray-500 text-sm border-b">
              <th className="pb-3 font-medium w-8">
                <input
                  type="checkbox"
                  checked={selectedSplits.length === splits.filter(s => s.locked_at && !s.paid_at).length}
                  onChange={selectAllLocked}
                  className="rounded"
                />
              </th>
              <th className="pb-3 font-medium">Order #</th>
              <th className="pb-3 font-medium">Dealer</th>
              <th className="pb-3 font-medium">Order Total</th>
              <th className="pb-3 font-medium">Commission Pool</th>
              <th className="pb-3 font-medium">Split Breakdown</th>
              <th className="pb-3 font-medium">Status</th>
              <th className="pb-3 font-medium">Date</th>
            </tr>
          </thead>
          <tbody>
            {splits.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-8 text-center text-gray-500">
                  No commission splits found
                </td>
              </tr>
            ) : (
              splits.map((split) => (
                <React.Fragment key={split.id}>
                  <tr className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedRow(expandedRow === split.id ? null : split.id)}>
                    <td className="py-3" onClick={(e) => e.stopPropagation()}>
                      {split.locked_at && !split.paid_at && (
                        <input
                          type="checkbox"
                          checked={selectedSplits.includes(split.id)}
                          onChange={() => toggleSelect(split.id)}
                          className="rounded"
                        />
                      )}
                    </td>
                    <td className="py-3">
                      <span className="font-medium">{split.order_number}</span>
                      <p className="text-xs text-gray-500">{split.order_status}</p>
                    </td>
                    <td className="py-3">
                      <span>{split.dealer_company || `${split.dealer_first_name} ${split.dealer_last_name}`}</span>
                      {split.dealer_type && (
                        <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${split.dealer_type === 'cedia_industry' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}`}>
                          {split.dealer_type === 'cedia_industry' ? 'CEDIA' : 'Core'}
                        </span>
                      )}
                    </td>
                    <td className="py-3">${Number(split.order_total || 0).toLocaleString()}</td>
                    <td className="py-3">
                      <span className="font-medium">${Number(split.total_commission_amount || 0).toLocaleString()}</span>
                      <span className="text-gray-500 text-sm ml-1">({split.total_commission_percent}%)</span>
                    </td>
                    <td className="py-3">
                      <div className="flex flex-wrap gap-1">
                        {(split.splits || []).slice(0, 3).map((s, idx) => (
                          <span key={idx} className={`text-xs px-1.5 py-0.5 rounded ${getRoleBadgeColor(s.role)}`}>
                            {formatRole(s.role)}: {s.percent}%
                          </span>
                        ))}
                        {(split.splits || []).length > 3 && (
                          <span className="text-xs text-gray-500">+{split.splits.length - 3} more</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3">
                      <div className="flex gap-1">
                        {split.locked_at ? (
                          <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">Locked</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-700">Unlocked</span>
                        )}
                        {split.paid_at && (
                          <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">Paid</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 text-gray-500 text-sm">
                      {new Date(split.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                  {expandedRow === split.id && (
                    <tr className="bg-gray-50">
                      <td colSpan={8} className="py-4 px-6">
                        <div className="grid grid-cols-4 gap-4">
                          {(split.splits || []).map((s, idx) => (
                            <div key={idx} className="bg-white p-3 rounded-lg border">
                              <div className="flex justify-between items-start mb-2">
                                <span className={`text-xs px-2 py-0.5 rounded ${getRoleBadgeColor(s.role)}`}>
                                  {formatRole(s.role)}
                                </span>
                                <span className="font-bold">{s.percent}%</span>
                              </div>
                              <p className="font-medium text-gray-900">{s.name || 'Unknown'}</p>
                              <p className="text-lg font-bold text-green-600">${Number(s.amount || 0).toLocaleString()}</p>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Leads Overview Tab (Factory Admin - View Only)
interface Lead {
  id: string;
  company_name: string;
  contact_first_name: string;
  contact_last_name: string;
  email: string;
  phone: string;
  status: string;
  source: string;
  lead_type: string;
  territory_code: string;
  territory_name: string;
  assigned_first_name: string;
  assigned_last_name: string;
  activity_count: number;
  created_at: string;
}

interface LeadStats {
  by_rep: Array<{
    id: string;
    first_name: string;
    last_name: string;
    total_leads: number;
    won_count: number;
    active_count: number;
  }>;
  by_territory: Array<{
    id: string;
    code: string;
    name: string;
    total_leads: number;
    won_count: number;
  }>;
  pipeline: Array<{
    status: string;
    count: number;
  }>;
  avg_activities_to_convert: string | null;
}

const leadStatusColors: Record<string, string> = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-yellow-100 text-yellow-800',
  qualified: 'bg-purple-100 text-purple-800',
  proposal_sent: 'bg-indigo-100 text-indigo-800',
  negotiating: 'bg-orange-100 text-orange-800',
  won: 'bg-green-100 text-green-800',
  lost: 'bg-red-100 text-red-800',
  converted: 'bg-emerald-100 text-emerald-800',
};

const leadStatusLabels: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  proposal_sent: 'Proposal Sent',
  negotiating: 'Negotiating',
  won: 'Won',
  lost: 'Lost',
  converted: 'Converted',
};

function LeadsOverviewTab() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [stats, setStats] = useState<LeadStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    assigned_to: '',
    source: '',
  });
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
      if (filters.source) params.append('source', filters.source);

      const [leadsData, statsData, repsData] = await Promise.all([
        apiRequest(`/leads?${params.toString()}`),
        apiRequest('/leads/admin-stats'),
        apiRequest('/users/sales-rep'),
      ]);

      setLeads(leadsData);
      setStats(statsData);
      setSalesReps(repsData.users || []);
    } catch (err) {
      console.error('Failed to load leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const headers = ['Company', 'Contact', 'Email', 'Phone', 'Status', 'Source', 'Territory', 'Assigned To', 'Created'];
    const rows = leads.map(lead => [
      lead.company_name,
      `${lead.contact_first_name} ${lead.contact_last_name}`,
      lead.email || '',
      lead.phone || '',
      leadStatusLabels[lead.status] || lead.status,
      lead.source || '',
      lead.territory_code || '',
      `${lead.assigned_first_name} ${lead.assigned_last_name}`,
      new Date(lead.created_at).toLocaleDateString(),
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return <div className="flex justify-center py-8"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Dashboard */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-600 font-medium">Total Leads</p>
            <p className="text-2xl font-bold text-blue-900">{leads.length}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-sm text-green-600 font-medium">Won/Converted</p>
            <p className="text-2xl font-bold text-green-900">
              {(stats.by_rep || []).reduce((sum, r) => sum + parseInt(String(r.won_count)), 0)}
            </p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-sm text-purple-600 font-medium">Active Pipeline</p>
            <p className="text-2xl font-bold text-purple-900">
              {(stats.pipeline || []).reduce((sum, p) => sum + parseInt(String(p.count)), 0)}
            </p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4">
            <p className="text-sm text-orange-600 font-medium">Avg Activities to Convert</p>
            <p className="text-2xl font-bold text-orange-900">
              {stats.avg_activities_to_convert || 'N/A'}
            </p>
          </div>
        </div>
      )}

      {/* Leaderboard */}
      {stats && stats.by_rep && stats.by_rep.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Leaderboard - Conversions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {stats.by_rep.slice(0, 5).map((rep, idx) => (
              <div key={rep.id} className="bg-white rounded-lg p-3 border flex items-center gap-3">
                <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${idx === 0 ? 'bg-yellow-100 text-yellow-700' : idx === 1 ? 'bg-gray-200 text-gray-700' : idx === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>
                  {idx + 1}
                </span>
                <div>
                  <p className="font-medium text-gray-900">{rep.first_name} {rep.last_name}</p>
                  <p className="text-sm text-gray-500">{rep.won_count} won | {rep.active_count} active</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="qualified">Qualified</option>
            <option value="proposal_sent">Proposal Sent</option>
            <option value="negotiating">Negotiating</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
            <option value="converted">Converted</option>
          </select>

          <select
            value={filters.assigned_to}
            onChange={(e) => setFilters({ ...filters, assigned_to: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All Sales Reps</option>
            {salesReps.map(rep => (
              <option key={rep.id} value={rep.id}>
                {rep.first_name} {rep.last_name}
              </option>
            ))}
          </select>

          <select
            value={filters.source}
            onChange={(e) => setFilters({ ...filters, source: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All Sources</option>
            <option value="website">Website</option>
            <option value="referral">Referral</option>
            <option value="trade_show">Trade Show</option>
            <option value="cold_call">Cold Call</option>
            <option value="linkedin">LinkedIn</option>
            <option value="advertisement">Advertisement</option>
            <option value="other">Other</option>
          </select>
        </div>

        <button
          onClick={exportCSV}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
        >
          Export CSV
        </button>
      </div>

      {/* Leads Table (View Only) */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-gray-500 text-sm border-b">
              <th className="pb-3 font-medium">Company</th>
              <th className="pb-3 font-medium">Contact</th>
              <th className="pb-3 font-medium">Status</th>
              <th className="pb-3 font-medium">Source</th>
              <th className="pb-3 font-medium">Territory</th>
              <th className="pb-3 font-medium">Assigned To</th>
              <th className="pb-3 font-medium">Activities</th>
              <th className="pb-3 font-medium">Added</th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-8 text-center text-gray-500">
                  No leads found
                </td>
              </tr>
            ) : (
              leads.map((lead) => (
                <tr key={lead.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 font-medium">{lead.company_name}</td>
                  <td className="py-3">
                    <div>
                      <span>{lead.contact_first_name} {lead.contact_last_name}</span>
                      {lead.email && <p className="text-sm text-gray-500">{lead.email}</p>}
                    </div>
                  </td>
                  <td className="py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${leadStatusColors[lead.status] || 'bg-gray-100 text-gray-800'}`}>
                      {leadStatusLabels[lead.status] || lead.status}
                    </span>
                  </td>
                  <td className="py-3 text-gray-600">
                    {lead.source ? lead.source.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : '-'}
                  </td>
                  <td className="py-3">{lead.territory_code || '-'}</td>
                  <td className="py-3">{lead.assigned_first_name} {lead.assigned_last_name}</td>
                  <td className="py-3 text-center">{lead.activity_count || 0}</td>
                  <td className="py-3 text-gray-500 text-sm">
                    {new Date(lead.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Rep Firm Payouts Tab
const levelLabels: Record<string, string> = {
  owner_override: 'Sales Leadership (2%)',
  rep_firm: 'Rep Firm — Shades (10%)',
  rep_firm_accessories: 'Rep Firm — Accessories (5%)',
  sales_rep_shades: 'Sales Rep — Shades (10%)',
  sales_rep_accessories: 'Sales Rep — Accessories (5%)',
  adjustment: 'Adjustment',
};

const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

function RepFirmPayoutsTab({ scope = 'rep-firm' }: { scope?: 'rep-firm' | 'internal' }) {
  const [summary, setSummary] = useState<any>(null);
  const [details, setDetails] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [view, setView] = useState<'summary' | 'details'>('summary');
  const [detailLabel, setDetailLabel] = useState('');
  const [detailFilter, setDetailFilter] = useState<any>({});
  const [statusFilter, setStatusFilter] = useState('earned');
  const [msg, setMsg] = useState('');
  const [showAdjustment, setShowAdjustment] = useState(false);
  const [adjAmount, setAdjAmount] = useState('');
  const [adjNote, setAdjNote] = useState('');
  const [adjOrderId, setAdjOrderId] = useState('');
  const [payRow, setPayRow] = useState<any>(null);
  const [payMethod, setPayMethod] = useState<'check' | 'ach' | 'cash' | 'wire' | 'other'>('check');
  const [payReference, setPayReference] = useState('');
  const [payNotes, setPayNotes] = useState('');
  // When marking the LAST row in a batch (e.g. last of 5 rows on Check #1234),
  // tick this to fire ONE email to the rep with all matching paid rows.
  // Defaults off so the box has to be intentionally checked.
  const [paySendStatement, setPaySendStatement] = useState(false);
  const [paySaving, setPaySaving] = useState(false);

  const token = localStorage.getItem('admin_auth_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  const loadSummary = async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams();
      if (statusFilter) qs.set('status', statusFilter);
      const res = await fetch(`${API_BASE}/rep-firms/commissions/payouts?${qs}`, { headers });
      if (!res.ok) throw new Error('Failed');
      setSummary(await res.json());
    } catch { } finally { setLoading(false); }
  };

  const loadDetails = async (filter: any, label: string) => {
    try {
      setDetailLoading(true);
      setDetailFilter(filter);
      setDetailLabel(label);
      setView('details');
      setSelectedIds(new Set());
      const qs = new URLSearchParams();
      if (filter.rep_firm_id) qs.set('rep_firm_id', filter.rep_firm_id);
      if (filter.sales_rep_id) qs.set('sales_rep_id', filter.sales_rep_id);
      // Which commission LAYER this drill-down is for. The Sales Leadership
      // (2%) and Direct Sales Rep tables are both keyed by sales_rep_id, so
      // without this they opened the exact same list.
      if (filter.commission_level) qs.set('commission_level', filter.commission_level);
      if (statusFilter) qs.set('status', statusFilter);
      const res = await fetch(`${API_BASE}/rep-firms/commissions/details?${qs}`, { headers });
      if (!res.ok) throw new Error('Failed');
      setDetails(await res.json());
    } catch { } finally { setDetailLoading(false); }
  };

  const handleMarkPaid = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Mark ${selectedIds.size} commission(s) as paid?`)) return;
    try {
      const res = await fetch(`${API_BASE}/rep-firms/commissions/mark-paid`, {
        method: 'POST', headers, body: JSON.stringify({ commission_ids: Array.from(selectedIds) }),
      });
      const result = await res.json();
      setMsg(`Marked ${result.updated} commission(s) as paid`);
      setTimeout(() => setMsg(''), 3000);
      setSelectedIds(new Set());
      loadSummary();
      if (view === 'details') loadDetails(detailFilter, detailLabel);
    } catch (err: any) { setMsg(`Error: ${err.message}`); }
  };

  const handleToggleVerify = async (row: any) => {
    const undo = !!row.verified_at;
    try {
      const res = await fetch(`${API_BASE}/rep-firms/commissions/${row.id}/verify`, {
        method: 'PATCH', headers, body: JSON.stringify({ undo }),
      });
      if (!res.ok) throw new Error((await res.json()).message || 'Failed');
      setMsg(undo ? 'Verification cleared' : 'Marked verified');
      setTimeout(() => setMsg(''), 2500);
      loadDetails(detailFilter, detailLabel);
    } catch (err: any) { setMsg(`Error: ${err.message}`); }
  };

  const openPayModal = (row: any) => {
    setPayRow(row);
    setPayMethod('check');
    setPayReference('');
    setPayNotes('');
  };

  const handleRecordPayment = async () => {
    if (!payRow) return;
    if (payMethod === 'check' && !payReference.trim()) {
      setMsg('Error: Check number required');
      return;
    }
    setPaySaving(true);
    try {
      const res = await fetch(`${API_BASE}/rep-firms/commissions/${payRow.id}/mark-paid`, {
        method: 'PATCH', headers,
        body: JSON.stringify({
          payment_method: payMethod,
          payment_reference: payReference.trim() || undefined,
          payment_notes: payNotes.trim() || undefined,
          send_statement: paySendStatement,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).message || 'Failed');
      setMsg(`Recorded ${payMethod} payment for ${payRow.order_number}${paySendStatement ? ' (statement emailed to rep)' : ''}`);
      setTimeout(() => setMsg(''), 3000);
      setPayRow(null);
      setPaySendStatement(false); // reset for next time
      loadSummary();
      loadDetails(detailFilter, detailLabel);
    } catch (err: any) { setMsg(`Error: ${err.message}`); }
    finally { setPaySaving(false); }
  };

  const handleUnmarkPaid = async (row: any) => {
    if (!confirm(`Roll back paid status on ${row.order_number}? This is for mistake recovery.`)) return;
    try {
      const res = await fetch(`${API_BASE}/rep-firms/commissions/${row.id}/unmark-paid`, {
        method: 'PATCH', headers,
      });
      if (!res.ok) throw new Error((await res.json()).message || 'Failed');
      setMsg('Reverted to earned');
      setTimeout(() => setMsg(''), 2500);
      loadSummary();
      loadDetails(detailFilter, detailLabel);
    } catch (err: any) { setMsg(`Error: ${err.message}`); }
  };

  const handleAdjustment = async () => {
    const amount = parseFloat(adjAmount);
    if (!amount || !adjNote || !adjOrderId) return;
    try {
      await fetch(`${API_BASE}/rep-firms/commissions/adjustment`, {
        method: 'POST', headers,
        body: JSON.stringify({
          order_id: adjOrderId,
          sales_rep_id: detailFilter.sales_rep_id || undefined,
          rep_firm_id: detailFilter.rep_firm_id || undefined,
          amount: -Math.abs(amount), // Always negative for deductions
          note: adjNote,
        }),
      });
      setMsg(`Deduction of ${fmt(Math.abs(amount))} applied`);
      setTimeout(() => setMsg(''), 3000);
      setShowAdjustment(false);
      setAdjAmount(''); setAdjNote(''); setAdjOrderId('');
      loadSummary();
      loadDetails(detailFilter, detailLabel);
    } catch (err: any) { setMsg(`Error: ${err.message}`); }
  };

  const handlePrintStatement = () => {
    if (details.length === 0) return;
    const now = new Date();
    const monthYear = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const commissionRows = details.filter((c: any) => c.commission_level !== 'adjustment');
    const adjustments = details.filter((c: any) => c.commission_level === 'adjustment');
    const subtotal = commissionRows.reduce((s: number, c: any) => s + Number(c.commission_amount), 0);
    const adjTotal = adjustments.reduce((s: number, c: any) => s + Number(c.commission_amount), 0);
    const total = subtotal + adjTotal;

    const w = window.open('', '_blank', 'width=800,height=900');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>Commission Statement — ${detailLabel}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
  h1 { font-size: 22px; margin-bottom: 4px; }
  .subtitle { color: #666; font-size: 14px; margin-bottom: 24px; }
  .header-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; }
  .company { font-size: 12px; color: #999; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 20px; }
  th { background: #f5f5f5; text-align: left; padding: 8px 10px; border-bottom: 2px solid #ddd; font-size: 11px; text-transform: uppercase; color: #666; }
  td { padding: 7px 10px; border-bottom: 1px solid #eee; }
  .right { text-align: right; }
  .bold { font-weight: bold; }
  .adj-row td { background: #fff8f8; color: #b91c1c; }
  .total-row { border-top: 2px solid #333; }
  .total-row td { padding-top: 10px; font-size: 15px; font-weight: bold; }
  .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 11px; color: #999; }
  .sig-line { margin-top: 60px; display: flex; gap: 60px; }
  .sig-line div { flex: 1; border-top: 1px solid #333; padding-top: 6px; font-size: 12px; color: #666; }
  @media print { body { margin: 20px; } button { display: none; } }
</style></head><body>
<div class="header-row">
  <div>
    <h1>Commission Statement</h1>
    <div class="subtitle">${detailLabel} — ${monthYear}</div>
  </div>
  <div style="text-align:right">
    <div class="company"><strong>Keder Screens</strong><br/>International Shade Products Inc<br/>12446 State Highway 205<br/>Lavon, TX 75166</div>
  </div>
</div>
<button onclick="window.print()" style="margin-bottom:20px;padding:8px 20px;background:#2563eb;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:14px;">Print</button>
<table>
  <thead><tr>
    <th>Order</th><th>Dealer</th><th>Type</th><th class="right">Base Amount</th><th class="right">Rate</th><th class="right">Commission</th>
  </tr></thead>
  <tbody>
${commissionRows.map((c: any) => `    <tr>
      <td>${c.order_number}</td>
      <td>${c.dealer_company}</td>
      <td>${levelLabels[c.commission_level] || c.commission_level}</td>
      <td class="right">${fmt(Number(c.base_amount))}</td>
      <td class="right">${Number(c.commission_percent)}%</td>
      <td class="right">${fmt(Number(c.commission_amount))}</td>
    </tr>`).join('\n')}
${commissionRows.length > 0 ? `    <tr><td colspan="5" class="right bold">Subtotal:</td><td class="right bold">${fmt(subtotal)}</td></tr>` : ''}
${adjustments.map((c: any) => `    <tr class="adj-row">
      <td>${c.order_number}</td>
      <td colspan="4">${c.clawback_reason || 'Adjustment'}</td>
      <td class="right bold">${fmt(Number(c.commission_amount))}</td>
    </tr>`).join('\n')}
    <tr class="total-row"><td colspan="5" class="right">Total Due:</td><td class="right">${fmt(total)}</td></tr>
  </tbody>
</table>
<div class="sig-line">
  <div>Approved By / Date</div>
  <div>Received By / Date</div>
</div>
<div class="footer">Generated ${now.toLocaleString()} — Keder Screens Commission System</div>
</body></html>`);
    w.document.close();
  };

  useEffect(() => {
    loadSummary();
    if (view === 'details') loadDetails(detailFilter, detailLabel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  if (loading && !summary) return <LoadingSpinner />;

  const renderTable = (title: string, color: string, rows: any[], isRepFirm: boolean, commissionLevel?: string) => {
    if (rows.length === 0) return (
      <div className="mb-6">
        <h3 className={`text-sm font-semibold ${color} mb-2`}>{title}</h3>
        <p className="text-sm text-gray-400 italic">No commissions yet</p>
      </div>
    );
    return (
      <div className="mb-6">
        <h3 className={`text-sm font-semibold ${color} mb-2`}>{title}</h3>
        <table className="min-w-full text-sm">
          <thead><tr className="border-b text-left text-gray-500">
            <th className="py-2">{isRepFirm ? 'Rep Firm' : 'Sales Rep'}</th>
            <th className="py-2 text-right">Orders</th>
            <th className="py-2 text-right">Total Sales</th>
            <th className="py-2 text-right" title="Commission accrued but order not yet shipped + paid in full">Pending</th>
            <th className="py-2 text-right" title="Commission earned (shipped + paid in full), not yet paid out">Owed</th>
            <th className="py-2 text-right" title="Paid commissions in this section only">Paid</th>
            {!isRepFirm && (
              <th className="py-2 text-right text-green-700 font-semibold" title="Total cash-out across every level (direct + adjustments + sales leadership 2%). Matches what was actually written to the rep on checks.">All-In Paid</th>
            )}
            <th className="py-2 text-right"></th>
          </tr></thead>
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.id} className="border-b hover:bg-gray-50">
                <td className="py-2 font-medium">{isRepFirm ? r.name : `${r.first_name} ${r.last_name}`}</td>
                <td className="py-2 text-right">{r.order_count}</td>
                <td className="py-2 text-right">{fmt(Number(r.total_sales))}</td>
                <td className="py-2 text-right text-amber-700">{fmt(Number(r.pending || 0))}</td>
                <td className="py-2 text-right font-medium text-green-700">{fmt(Number(r.owed))}</td>
                <td className="py-2 text-right text-gray-500">{fmt(Number(r.paid))}</td>
                {!isRepFirm && (
                  <td className="py-2 text-right text-green-700 font-bold">{fmt(Number(r.paid_all_levels ?? r.paid))}</td>
                )}
                <td className="py-2 text-right">
                  <button onClick={() => loadDetails(
                    isRepFirm
                      ? { rep_firm_id: r.id, commission_level: commissionLevel }
                      : { sales_rep_id: r.id, commission_level: commissionLevel },
                    // Name the layer in the header — a rep can appear in both
                    // the 2% leadership table and the direct table, and the
                    // two drill-downs looked identical without it.
                    `${isRepFirm ? r.name : `${r.first_name} ${r.last_name}`}${
                      commissionLevel === 'owner_override' ? ' — Sales Leadership Override (2%)'
                      : commissionLevel === 'sales_rep' ? ' — Direct Sales Rep Commissions'
                      : commissionLevel === 'rep_firm' ? ' — Rep Firm Commissions'
                      : ''}`,
                  )} className="text-blue-600 hover:text-blue-800 text-xs">View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {view === 'details' && (
            <button onClick={() => setView('summary')} className="text-sm text-blue-600 hover:text-blue-800">
              &larr; Back to Summary
            </button>
          )}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-2 py-1 text-sm border rounded">
            <option value="">All</option>
            <option value="pending">Pending (not yet shipped/paid)</option>
            <option value="earned">Earned (Owed)</option>
            <option value="paid">Paid</option>
          </select>
        </div>
        {view === 'details' && (
          <div className="flex items-center gap-2">
            <button onClick={handlePrintStatement}
              className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded text-sm font-medium hover:bg-blue-200">
              Print Statement
            </button>
            <button onClick={() => setShowAdjustment(!showAdjustment)}
              className="px-3 py-1.5 bg-red-100 text-red-700 rounded text-sm font-medium hover:bg-red-200">
              Deduction
            </button>
            {selectedIds.size > 0 && (
              <button onClick={handleMarkPaid}
                className="px-3 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700">
                Mark {selectedIds.size} as Paid
              </button>
            )}
          </div>
        )}
      </div>

      {msg && <div className={`mb-3 p-2 rounded text-sm ${msg.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{msg}</div>}

      {showAdjustment && view === 'details' && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h4 className="text-sm font-semibold text-red-800 mb-3">Add Deduction</h4>
          <div className="flex items-end gap-3">
            <div>
              <label className="block text-xs text-red-700 mb-1">Order</label>
              <select value={adjOrderId} onChange={e => setAdjOrderId(e.target.value)}
                className="px-2 py-1.5 text-sm border border-red-300 rounded">
                <option value="">Select order...</option>
                {[...new Set(details.map((d: any) => d.order_id))].map(oid => {
                  const d = details.find((x: any) => x.order_id === oid);
                  return <option key={oid} value={oid}>{d?.order_number} — {d?.dealer_company}</option>;
                })}
              </select>
            </div>
            <div>
              <label className="block text-xs text-red-700 mb-1">Amount ($)</label>
              <input type="number" step="0.01" value={adjAmount} onChange={e => setAdjAmount(e.target.value)}
                placeholder="550.00" className="px-2 py-1.5 text-sm border border-red-300 rounded w-28" />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-red-700 mb-1">Reason</label>
              <input type="text" value={adjNote} onChange={e => setAdjNote(e.target.value)}
                placeholder="Somfy upcharge waived" className="px-2 py-1.5 text-sm border border-red-300 rounded w-full" />
            </div>
            <button onClick={handleAdjustment} disabled={!adjOrderId || !adjAmount || !adjNote}
              className="px-4 py-1.5 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 disabled:opacity-50">
              Apply
            </button>
            <button onClick={() => setShowAdjustment(false)}
              className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700">
              Cancel
            </button>
          </div>
        </div>
      )}

      {view === 'summary' && summary && (
        <>
          {scope === 'rep-firm' && (
            renderTable('Rep Firm Commissions (10% shades / 5% accessories)', 'text-purple-700', summary.repFirms, true, 'rep_firm')
          )}
          {scope === 'internal' && (
            <>
              {renderTable('Sales Leadership Override (2% on rep firm orders)', 'text-amber-700', summary.salesLeadership, false, 'owner_override')}
              {renderTable('Direct Sales Rep Commissions (no rep firm)', 'text-blue-700', summary.salesReps, false, 'sales_rep')}
            </>
          )}
        </>
      )}

      {view === 'details' && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">{detailLabel} — {details.length} records</h3>
          {detailLoading ? <LoadingSpinner /> : details.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No commission records</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead><tr className="border-b text-left text-gray-500">
                  <th className="py-2 pr-2">
                    <input type="checkbox"
                      checked={selectedIds.size > 0 && selectedIds.size === details.filter(d => d.status === 'earned').length}
                      onChange={() => {
                        const earned = details.filter(d => d.status === 'earned').map(d => d.id);
                        setSelectedIds(prev => prev.size === earned.length ? new Set() : new Set(earned));
                      }} className="rounded" />
                  </th>
                  <th className="py-2">Order</th>
                  <th className="py-2">Dealer</th>
                  <th className="py-2">Layer</th>
                  <th className="py-2 text-right">Base</th>
                  <th className="py-2 text-right">Rate</th>
                  <th className="py-2 text-right">Commission</th>
                  <th className="py-2 text-center">Status</th>
                  <th className="py-2 text-center">Verified</th>
                  <th className="py-2">Payment</th>
                  <th className="py-2">Shipped</th>
                </tr></thead>
                <tbody>
                  {details.map((c: any) => {
                    const isPaid = c.status === 'paid';
                    const isEarned = c.status === 'earned';
                    const isAdjustment = c.commission_level === 'adjustment';
                    return (
                    <tr key={c.id} className={`border-b hover:bg-gray-50 ${selectedIds.has(c.id) ? 'bg-blue-50' : ''}`}>
                      <td className="py-2 pr-2">
                        {isEarned && !isAdjustment && <input type="checkbox" checked={selectedIds.has(c.id)} onChange={() => toggleSelect(c.id)} className="rounded" />}
                      </td>
                      <td className="py-2">
                        <span className="font-medium">{c.order_number}</span>
                        <span className="text-gray-500 ml-1 text-xs">{c.sidemark}</span>
                      </td>
                      <td className="py-2">{c.dealer_company}</td>
                      <td className="py-2 text-gray-600">{levelLabels[c.commission_level] || c.commission_level}</td>
                      <td className="py-2 text-right">{fmt(Number(c.base_amount))}</td>
                      <td className="py-2 text-right">{Number(c.commission_percent)}%</td>
                      <td className="py-2 text-right font-medium">{fmt(Number(c.commission_amount))}</td>
                      <td className="py-2 text-center">
                        <StatusBadge status={c.status} />
                      </td>
                      <td className="py-2 text-center">
                        {isAdjustment ? (
                          <span className="text-gray-300 text-xs">—</span>
                        ) : (
                          <label className="inline-flex items-center gap-1 cursor-pointer" title={c.verified_by_name ? `Verified by ${c.verified_by_name} on ${new Date(c.verified_at).toLocaleString()}` : 'Mark as reviewed/approved before payout'}>
                            <input type="checkbox" checked={!!c.verified_at}
                              disabled={isPaid}
                              onChange={() => handleToggleVerify(c)}
                              className="rounded h-4 w-4 text-emerald-600 focus:ring-emerald-500" />
                            {c.verified_at && <span className="text-emerald-700 text-xs font-medium">✓</span>}
                          </label>
                        )}
                      </td>
                      <td className="py-2">
                        {isPaid ? (
                          <div className="flex items-center gap-2">
                            <div className="text-xs">
                              <div className="font-medium text-gray-700">
                                {c.payment_method ? c.payment_method.toUpperCase() : 'Paid'}
                                {c.payment_reference && <span className="ml-1 text-gray-500">#{c.payment_reference}</span>}
                              </div>
                              <div className="text-gray-400">{c.paid_at ? new Date(c.paid_at).toLocaleDateString() : ''}{c.paid_by_name ? ` · ${c.paid_by_name}` : ''}</div>
                            </div>
                            <button onClick={() => handleUnmarkPaid(c)}
                              className="text-xs text-red-600 hover:text-red-800 underline" title="Roll back if recorded by mistake">
                              undo
                            </button>
                          </div>
                        ) : isEarned && !isAdjustment ? (
                          <button onClick={() => openPayModal(c)}
                            className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 font-medium">
                            Record Payment
                          </button>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-2 text-gray-500">{c.shipped_at ? new Date(c.shipped_at).toLocaleDateString() : '—'}</td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {/* Split by status — "Paid" is what actually hit the rep's
                      pocket. "Earned" = order shipped/delivered + paid in full
                      but not yet cut. "Pending" = forecast (not yet earned).
                      Old single-line total mixed all three, made it look like
                      Kim was paid $2,423.77 when actual cash-out was $1,890.19. */}
                  {(() => {
                    const paid = details.filter((c: any) => c.status === 'paid').reduce((s: number, c: any) => s + Number(c.commission_amount), 0);
                    const earned = details.filter((c: any) => c.status === 'earned').reduce((s: number, c: any) => s + Number(c.commission_amount), 0);
                    const pending = details.filter((c: any) => c.status === 'pending').reduce((s: number, c: any) => s + Number(c.commission_amount), 0);
                    return (
                      <>
                        <tr className="border-t-2 bg-green-50">
                          <td colSpan={6} className="py-2 pr-2 text-right font-semibold text-green-900">Paid (actual cash-out):</td>
                          <td className="py-2 text-right font-bold text-green-900">{fmt(paid)}</td>
                          <td colSpan={4}></td>
                        </tr>
                        {earned > 0 && (
                          <tr className="bg-blue-50">
                            <td colSpan={6} className="py-1.5 pr-2 text-right text-sm text-blue-900">Earned (ready to pay):</td>
                            <td className="py-1.5 text-right font-semibold text-sm text-blue-900">{fmt(earned)}</td>
                            <td colSpan={4}></td>
                          </tr>
                        )}
                        {pending > 0 && (
                          <tr className="bg-gray-50">
                            <td colSpan={6} className="py-1.5 pr-2 text-right text-sm text-gray-600">Pending (forecast):</td>
                            <td className="py-1.5 text-right font-semibold text-sm text-gray-600">{fmt(pending)}</td>
                            <td colSpan={4}></td>
                          </tr>
                        )}
                      </>
                    );
                  })()}
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {payRow && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => !paySaving && setPayRow(null)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-1">Record Payment</h3>
            <div className="text-sm text-gray-600 mb-4">
              <div><span className="font-medium">{payRow.order_number}</span> · {payRow.dealer_company}</div>
              <div className="text-emerald-700 font-medium mt-1">Amount: {fmt(Number(payRow.commission_amount))}</div>
              {!payRow.verified_at && (
                <div className="mt-2 px-2 py-1 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded">
                  Heads up — this commission has not been verified yet. You can still record payment, but verification is the usual safeguard against double-paying.
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Method</label>
                <select value={payMethod} onChange={e => setPayMethod(e.target.value as any)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded">
                  <option value="check">Check</option>
                  <option value="ach">ACH / Direct Deposit</option>
                  <option value="cash">Cash</option>
                  <option value="wire">Wire</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {payMethod === 'check' ? 'Check Number' : payMethod === 'ach' ? 'ACH Confirmation #' : 'Reference #'}
                  {payMethod === 'check' && <span className="text-red-600 ml-1">*</span>}
                </label>
                <input type="text" value={payReference} onChange={e => setPayReference(e.target.value)}
                  placeholder={payMethod === 'check' ? '1234' : 'Optional'}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea value={payNotes} onChange={e => setPayNotes(e.target.value)}
                  rows={2} placeholder="e.g. mailed 5/12, included with monthly statement"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded resize-none" />
              </div>
              <div className="pt-2 border-t border-gray-200">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paySendStatement}
                    onChange={e => setPaySendStatement(e.target.checked)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Email statement to rep</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Sends ONE email listing every commission row with the same reference{payReference ? <> ({payReference})</> : ''}. Check this on the LAST row when paying multiple orders on one check; leave unchecked otherwise.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setPayRow(null)} disabled={paySaving}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                Cancel
              </button>
              <button onClick={handleRecordPayment} disabled={paySaving || (payMethod === 'check' && !payReference.trim())}
                className="px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 font-medium disabled:opacity-50">
                {paySaving ? 'Saving...' : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function _RepFirmContactsTabRemoved() {
  const [firms, setFirms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedFirm, setExpandedFirm] = useState<string | null>(null);
  const [contacts, setContacts] = useState<any[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState<string | null>(null);
  const [newContact, setNewContact] = useState({ email: '', firstName: '', lastName: '', phone: '', role: 'rep' });
  const [saving, setSaving] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);

  useEffect(() => {
    loadFirms();
  }, []);

  const loadFirms = async () => {
    try {
      const data = await apiRequest('/rep-firms');
      setFirms(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadContacts = async (firmId: string) => {
    setContactsLoading(true);
    try {
      const data = await apiRequest(`/rep-firms/${firmId}/contacts`);
      setContacts(data);
    } catch (err) { console.error(err); }
    finally { setContactsLoading(false); }
  };

  const toggleFirm = (firmId: string) => {
    if (expandedFirm === firmId) {
      setExpandedFirm(null);
      setContacts([]);
    } else {
      setExpandedFirm(firmId);
      loadContacts(firmId);
    }
  };

  const handleCreateContact = async () => {
    if (!showAddModal || !newContact.email) return;
    setSaving(true);
    try {
      const result = await apiRequest(`/rep-firms/${showAddModal}/contacts`, {
        method: 'POST',
        body: JSON.stringify(newContact),
      });
      setGeneratedPassword(result.password);
      loadContacts(showAddModal);
    } catch (err: any) {
      alert(err.message || 'Failed to create contact');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async (firmId: string, contactId: string, email: string) => {
    if (!window.confirm(`Reset password for ${email}?`)) return;
    try {
      const result = await apiRequest(`/rep-firms/${firmId}/contacts/${contactId}/reset-password`, { method: 'POST' });
      alert(`New password for ${email}:\n\n${result.password}\n\nSave this — it will not be shown again.`);
    } catch (err: any) {
      alert(err.message || 'Failed to reset password');
    }
  };

  const handleDeactivate = async (firmId: string, contactId: string, email: string) => {
    if (!window.confirm(`Deactivate ${email}?`)) return;
    try {
      await apiRequest(`/rep-firms/${firmId}/contacts/${contactId}`, { method: 'DELETE' });
      loadContacts(firmId);
    } catch (err: any) {
      alert(err.message || 'Failed to deactivate');
    }
  };

  const closeAddModal = () => {
    setShowAddModal(null);
    setNewContact({ email: '', firstName: '', lastName: '', phone: '', role: 'rep' });
    setGeneratedPassword(null);
  };

  if (loading) return <div className="flex justify-center py-8"><LoadingSpinner /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold">Rep Firm Portal Logins</h3>
          <p className="text-sm text-gray-500">Manage login accounts for rep firm users at reps.kederscreens.com</p>
        </div>
      </div>

      {firms.length === 0 ? (
        <p className="text-gray-500 py-4 text-center">No rep firms found</p>
      ) : (
        <div className="space-y-2">
          {firms.map((firm: any) => (
            <div key={firm.id} className="border rounded-lg overflow-hidden">
              {/* Firm header */}
              <button
                onClick={() => toggleFirm(firm.id)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="text-purple-600 font-semibold">{firm.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                    {firm.channel || 'window_treatment'}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${firm.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {firm.status}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowAddModal(firm.id); }}
                    className="px-3 py-1 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700"
                  >
                    + Add Login
                  </button>
                  <svg className={`w-5 h-5 text-gray-400 transition-transform ${expandedFirm === firm.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Contacts list */}
              {expandedFirm === firm.id && (
                <div className="border-t">
                  {contactsLoading ? (
                    <div className="p-4 text-center text-gray-400">Loading contacts...</div>
                  ) : contacts.length === 0 ? (
                    <div className="p-4 text-center text-gray-400">No portal logins yet. Click "+ Add Login" to create one.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 uppercase bg-gray-50/50">
                          <th className="px-4 py-2">Name</th>
                          <th className="px-4 py-2">Email</th>
                          <th className="px-4 py-2">Role</th>
                          <th className="px-4 py-2">Status</th>
                          <th className="px-4 py-2">Last Login</th>
                          <th className="px-4 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {contacts.map((c: any) => (
                          <tr key={c.id} className={c.status !== 'active' ? 'opacity-50' : ''}>
                            <td className="px-4 py-2.5 font-medium">
                              {c.firstName} {c.lastName}
                              {c.isPrimary && <span className="ml-1 text-xs text-green-600">(Primary)</span>}
                            </td>
                            <td className="px-4 py-2.5 text-gray-600">{c.email}</td>
                            <td className="px-4 py-2.5">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${c.role === 'owner' ? 'bg-amber-100 text-amber-700' : c.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                {c.role}
                              </span>
                            </td>
                            <td className="px-4 py-2.5">
                              <span className={c.status === 'active' ? 'text-green-600' : 'text-gray-400'}>{c.status}</span>
                            </td>
                            <td className="px-4 py-2.5 text-gray-400 text-xs">
                              {c.lastLoginAt ? new Date(c.lastLoginAt).toLocaleDateString() : 'Never'}
                            </td>
                            <td className="px-4 py-2.5 text-right space-x-2">
                              <button
                                onClick={() => handleResetPassword(firm.id, c.id, c.email)}
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                Reset PW
                              </button>
                              {c.status === 'active' && (
                                <button
                                  onClick={() => handleDeactivate(firm.id, c.id, c.email)}
                                  className="text-xs text-red-600 hover:text-red-800"
                                >
                                  Deactivate
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Contact Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            {generatedPassword ? (
              <>
                <h3 className="text-lg font-semibold text-green-700 mb-4">Login Created!</h3>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-600 mb-2">Portal URL:</p>
                  <p className="font-mono text-sm font-bold">https://reps.kederscreens.com</p>
                  <p className="text-sm text-gray-600 mt-3 mb-2">Email:</p>
                  <p className="font-mono text-sm font-bold">{newContact.email}</p>
                  <p className="text-sm text-gray-600 mt-3 mb-2">Password:</p>
                  <p className="font-mono text-lg font-bold text-green-800 select-all">{generatedPassword}</p>
                </div>
                <p className="text-xs text-red-600 mb-4">Save this password now — it will not be shown again.</p>
                <button onClick={closeAddModal} className="w-full py-2 bg-gray-800 text-white rounded-lg">Done</button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-4">Add Rep Firm Login</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email" value={newContact.email}
                      onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                      placeholder="rep@company.com"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <input
                        type="text" value={newContact.firstName}
                        onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <input
                        type="text" value={newContact.lastName}
                        onChange={(e) => setNewContact({ ...newContact, lastName: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel" value={newContact.phone}
                      onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <select
                      value={newContact.role}
                      onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                    >
                      <option value="owner">Owner (sees all firm data)</option>
                      <option value="admin">Admin</option>
                      <option value="rep">Rep</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={closeAddModal} className="flex-1 py-2 border rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button
                    onClick={handleCreateContact}
                    disabled={saving || !newContact.email}
                    className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {saving ? 'Creating...' : 'Create Login'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Main Sales Page
export default function SalesPage() {
  const [activeTab, setActiveTab] = useState<TabType>('reps');

  return (
    <>
      <PageHeader
        title="Sales & Commissions"
        subtitle="Manage sales reps, commissions, and dealer assignments"
      />

      <div className="flex gap-2 mb-6 flex-wrap">
        <TabButton active={activeTab === 'reps'} onClick={() => setActiveTab('reps')}>
          All Sales Reps
        </TabButton>
        <TabButton active={activeTab === 'leads'} onClick={() => setActiveTab('leads')}>
          Leads Overview
        </TabButton>
        <TabButton active={activeTab === 'commissions'} onClick={() => setActiveTab('commissions')}>
          Commission Reports
        </TabButton>
        <TabButton active={activeTab === 'splits'} onClick={() => setActiveTab('splits')}>
          Commission Splits
        </TabButton>
        <TabButton active={activeTab === 'clawbacks'} onClick={() => setActiveTab('clawbacks')}>
          Clawbacks
        </TabButton>
        <TabButton active={activeTab === 'payouts-rep'} onClick={() => setActiveTab('payouts-rep')}>
          Rep Firm Payouts
        </TabButton>
        <TabButton active={activeTab === 'payouts-internal'} onClick={() => setActiveTab('payouts-internal')}>
          Internal Sales Payouts
        </TabButton>
      </div>

      <Card>
        {activeTab === 'reps' && <SalesRepsTab />}
        {activeTab === 'leads' && <LeadsOverviewTab />}
        {activeTab === 'commissions' && <CommissionsTab />}
        {activeTab === 'splits' && <CommissionSplitsTab />}
        {activeTab === 'clawbacks' && <ClawbacksTab />}
        {activeTab === 'payouts-rep' && <RepFirmPayoutsTab scope="rep-firm" />}
        {activeTab === 'payouts-internal' && <RepFirmPayoutsTab scope="internal" />}
      </Card>
    </>
  );
}
